import { useMutation, useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDock, miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { CreateJournalEntryMutation, CreateJournalEntryMutationVariables, GetJournalEntryQuery, GetJournalEntryQueryVariables, RitualType, SubTask, SubTaskType, UpdateJournalEntryMutation, UpdateJournalEntryMutationVariables } from '../client/generated/graphql';
import { createJournalEntryCache, createJournalEntryMutation } from '../client/mutations/createJournalEntry';
import { updateJournalEntryMutation } from '../client/mutations/updateJournalEntry';
import { updateQuestMutation } from '../client/mutations/updateQuest';
import { getJournalEntryQuery } from '../client/queries/journalEntry';
import { JournalHeaderBanner } from '../components/journal/JournalHeaderBanner';
import { JournalPrompt } from '../components/journal/JournalPrompt';
import { JournalTextInput, type JournalTextInputRef } from '../components/journal/JournalTextInput';
import { JournalVoiceButton } from '../components/journal/JournalVoiceButton';
import { JournalEntryPageSkeleton } from '../components/journal/skeleton/JournalEntryPageSkeleton';
import { Page } from '../components/Page';
import { getRitualConfig } from '../helpers/ritual';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedQuestWithFallback } from '../hooks/caching/quest';

export const JournalEntryPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ritualId: ritualIdParam, journalEntryId: journalEntryIdParam } = useParams<{ ritualId?: string; journalEntryId?: string }>();
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const isEditingMode = !!journalEntryIdParam;

    const { data: journalEntryData, loading: journalEntryLoading } = useQuery<GetJournalEntryQuery, GetJournalEntryQueryVariables>(
        getJournalEntryQuery,
        {
            variables: { id: journalEntryIdParam || '' },
            skip: !journalEntryIdParam,
            fetchPolicy: 'cache-first',
            notifyOnNetworkStatusChange: true,
        }
    );

    const ritualId = isEditingMode 
        ? (journalEntryData?.journalEntry?.ritualId || ritualIdParam)
        : ritualIdParam;

    const { quest, loading: questLoading } = useCachedQuestWithFallback(ritualId);

    const journalTask = useMemo(() => {
        if (isEditingMode && journalEntryIdParam) {
            return quest?.subTasks?.find(t => t.journalEntryId === journalEntryIdParam);
        }
        return quest?.subTasks?.find(t => t.taskType === SubTaskType.Journal);
    }, [quest?.subTasks, isEditingMode, journalEntryIdParam]);

    const journalEntryId = journalEntryIdParam || journalTask?.journalEntryId;

    const [createJournalEntry] = useMutation<CreateJournalEntryMutation, CreateJournalEntryMutationVariables>(
        createJournalEntryMutation,
        {
            update: createJournalEntryCache,
        }
    );
    const [updateJournalEntry] = useMutation<UpdateJournalEntryMutation, UpdateJournalEntryMutationVariables>(
        updateJournalEntryMutation
    );
    const [updateQuest] = useMutation(updateQuestMutation);

    const existingEntry = journalEntryData?.journalEntry;
    const isEditing = !!existingEntry;

    const ritualType = (existingEntry?.ritualType as RitualType | undefined) 
        || (quest?.ritualType as RitualType | undefined);
    const config = useMemo(
        () => {
            const baseConfig = ritualType ? getRitualConfig(t)[ritualType] : null;
            return baseConfig ? { ...baseConfig, icon: 'description' } : null;
        },
        [ritualType, t],
    );

    const taskName = isEditingMode
        ? (existingEntry?.taskName || t('journal.entry'))
        : (journalTask?.name || t('journal.entry'));
    
    const promptText = existingEntry?.promptText || journalTask?.promptText || '';

    useEffect(() => {
        if (journalEntryData?.journalEntry?.text && text === '') {
            setText(journalEntryData.journalEntry.text);
        } else if (!journalEntryId && !questLoading && !journalEntryLoading && text === '') {
            setText('');
        }
    }, [journalEntryData?.journalEntry?.id, journalEntryData?.journalEntry?.text, journalEntryId, questLoading, journalEntryLoading, text]);

    const handleSave = useCallback(async () => {
        if (!text.trim() || !ritualId || !quest) return;
        
        setSaving(true);
        try {
            if (isEditing && existingEntry) {
                await updateJournalEntry({
                    variables: {
                        id: existingEntry.id,
                        data: { text: text.trim() },
                    },
                });
            } else if (!isEditingMode && ritualId && quest) {
                const result = await createJournalEntry({
                    variables: {
                        data: {
                            text: text.trim(),
                            ritualId,
                            ritualType: ritualType ?? undefined,
                            taskName: journalTask?.name ?? undefined,
                            promptText: journalTask?.promptText ?? undefined,
                        },
                    },
                });

                if (result.data?.createJournalEntry && quest.subTasks) {
                    const journalTaskIndex = quest.subTasks.findIndex(
                        t => t.taskType === SubTaskType.Journal && !t.done
                    );

                    if (journalTaskIndex >= 0) {
                        const updatedSubTasks = quest.subTasks.map((t, i) => {
                            const task: SubTask = {
                                name: t.name,
                                done: i === journalTaskIndex ? true : t.done,
                            };
                            if (t.taskType) task.taskType = t.taskType;
                            if (t.promptText) task.promptText = t.promptText;
                            if (i === journalTaskIndex && result.data?.createJournalEntry?.id) {
                                task.journalEntryId = result.data.createJournalEntry.id;
                            } else if (t.journalEntryId) {
                                task.journalEntryId = t.journalEntryId;
                            }
                            if (t.ratingEntryId) task.ratingEntryId = t.ratingEntryId;
                            return task;
                        });

                        await updateQuest({
                            variables: {
                                questId: ritualId,
                                name: quest.name,
                                description: quest.description,
                                categoryId: quest.categoryId,
                                difficulty: quest.difficulty,
                                goalId: quest.goalId ?? undefined,
                                timing: quest.timing ?? false,
                                timedInterval: quest.timedInterval ?? 0,
                                timerValueInMs: quest.timerValueInMs ?? 0,
                                repeat: quest.repeat ? {
                                    query: quest.repeat.query,
                                    repeatingType: quest.repeat.repeatingType,
                                    interval: quest.repeat.interval ?? undefined,
                                    weekDays: quest.repeat.weekDays ?? [],
                                    monthDays: quest.repeat.monthDays ?? [],
                                    startHour: quest.repeat.startHour ?? undefined,
                                    startMinute: quest.repeat.startMinute ?? undefined,
                                } : undefined,
                                subTasks: updatedSubTasks,
                            },
                        });
                    }
                }
            }

            navigate(-1);
        } catch (error) {
            console.error('Failed to save journal entry:', error);
        } finally {
            setSaving(false);
        }
    }, [text, ritualId, quest, ritualType, isEditing, isEditingMode, existingEntry, journalTask, createJournalEntry, updateJournalEntry, updateQuest, journalEntryIdParam]);

    const isValid = useMemo(() => text.trim().length > 0 && !saving, [text, saving]);

    const navigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const journalInputRef = useRef<JournalTextInputRef>(null);
    const appendTranscript = useCallback((transcribed: string) => {
        setText((prev) => (prev ? `${prev}\n\n${transcribed}` : transcribed));
        requestAnimationFrame(() => {
            requestAnimationFrame(() => journalInputRef.current?.scrollToBottom());
        });
    }, []);

    const handleInputFocus = useCallback(() => {
        setIsInputFocused(true);
    }, []);

    const handleInputBlur = useCallback(() => {
        requestAnimationFrame(() => {
            const activeElement = document.activeElement as HTMLElement | null;
            if (!activeElement || activeElement.tagName !== 'TEXTAREA') {
                setIsInputFocused(false);
            }
        });
    }, []);

    const isLoading = isEditingMode
        ? (journalEntryLoading || !config || !existingEntry || (ritualId && questLoading && !quest))
        : (questLoading || !config || !journalTask);
    
    if (isLoading || !config) {
        return <JournalEntryPageSkeleton />;
    }

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: t('actions.cancel'), onClick: navigateBack, navigation: 'back' }}
                    main={{
                        text: isEditingMode ? t('actions.save') : t('journal.saveAndComplete'),
                        onClick: handleSave,
                        enabled: isValid,
                        loading: saving,
                        bgColor: isValid ? config.buttonBgEnabled : getButtonThemeColors(false).backgroundColor,
                    }}
                />
            }
        >
            <Stack height="100%" sx={{ background: 'var(--tgui--secondary_bg_color)', paddingTop: 'var(--tg-viewport-content-safe-area-inset-top, 0px)' }}>
                {!isInputFocused && <JournalHeaderBanner config={config} taskName={taskName} />}
                <Stack
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        padding: '16px',
                        overflow: isInputFocused ? 'hidden' : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {!isInputFocused && <JournalPrompt promptText={promptText} />}
                    <JournalTextInput
                        ref={journalInputRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                    />
                </Stack>
                <Stack
                    sx={{
                        flexShrink: 0,
                        padding: `0 16px calc(16px + ${miniButtonDockScrollBottomPad})`,
                    }}
                >
                    <JournalVoiceButton onTranscript={appendTranscript} config={config} />
                </Stack>
            </Stack>
        </Page>
    );
};
